/**
 * A file picker that names the file it holds.
 *
 * The import controls hid a bare `input type=file` inside a styled label, so
 * the chosen file was only visible in whatever summary the screen happened to
 * render, and a screen reader heard the button but never the outcome.
 *
 * @module components/ui/Form/FileField
 */

import React, { type ChangeEvent, useId, useRef } from "react";

import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";

export interface FileFieldProps {
  /** What the control picks, e.g. "Programme JSON". Its accessible name. */
  label: string;
  /** `accept` for the input, e.g. ".json,application/json". */
  accept?: string;
  /** The chosen file's name, so the control can report what it holds. */
  fileName?: string;
  /** Called with the chosen file, or null when the picker is cancelled. */
  onChange: (file: File | null) => void;
  /** Shown under the control, in the error tone, and clears on the next pick. */
  error?: string;
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <FileField label="Programme JSON" accept=".json" fileName={name} onChange={read} />
 * ```
 */
export function FileField({
  label,
  accept = undefined,
  fileName = undefined,
  onChange,
  error = undefined,
  disabled = false,
  disabledReason = undefined,
  "data-testid": testId,
}: FileFieldProps): React.ReactElement {
  const input = useRef<HTMLInputElement>(null);
  const statusId = useId();
  const errorId = useId();
  // Both, so the failure is named whenever the control is reached and not only
  // in the moment the alert fires.
  const describedBy = error ? `${statusId} ${errorId}` : statusId;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const picked = event.target.files?.[0] ?? null;
    // Cleared afterwards so picking the same filename again still fires a
    // change. Without it a failed import cannot be retried with the corrected
    // file: the value is unchanged, no event fires, and the error stays up.
    event.target.value = "";
    onChange(picked);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* A button rather than a styled label: a label is not focusable, so the
          picker was unreachable from a keyboard. */}
      <Button
        icon="upload-simple"
        disabled={disabled}
        disabledReason={disabledReason}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onClick={() => input.current?.click()}
      >
        {/* The label as written: lowercasing it turned "Programme JSON" into
            "programme json". */}
        {fileName ? `Choose another ${label}` : `Choose ${label}`}
      </Button>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        aria-label={label}
        aria-describedby={describedBy}
        disabled={disabled}
        data-testid={testId}
        onChange={handleChange}
      />
      {/* Announced, because the file is the whole outcome of using this. */}
      <p id={statusId} className="text-md text-muted" aria-live="polite">
        {fileName ?? "No file chosen"}
      </p>
      {/* `role="alert"` rather than a derived testid: `<id>-error` makes a
          `^=` query on the caller's own id match two elements, and the error
          is what a reader needs told when it appears. */}
      {error && (
        <p id={errorId} role="alert" className="flex items-center gap-1 text-md text-error-strong">
          <Icon name="warning-circle" aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}
